import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Maximize2, X } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, Area, AreaChart, ComposedChart, Cell } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import ChartModal from './ChartModal';
import { useFilters } from '../contexts/FilterContext';
import { fuelAnalyticsAPI } from '../services/api';

// Helper to format dates for API in local time
const formatDateForApi = (date) => {
  if (!(date instanceof Date)) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper to format date for chart display
// Handles both "YYYY-MM" (monthly -> "Dec 2023") and "YYYY-MM-DD" (daily -> "16 Mar")
const formatDateForChart = (dateStr) => {
  if (!dateStr) return '';
  // Check if it's a monthly format (YYYY-MM without day)
  if (/^\d{4}-\d{2}$/.test(dateStr)) {
    const [year, monthNum] = dateStr.split('-');
    const date = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
    const month = date.toLocaleString('default', { month: 'short' });
    return `${month} ${year}`;
  }
  const date = new Date(dateStr);
  const day = date.getDate();
  const month = date.toLocaleString('default', { month: 'short' });
  return `${day} ${month}`;
};


const Fuel = ({ activeTab = 'Fuel' }) => {
  const { filters, manualTrigger, setAvailableMachines } = useFilters();

  // State management for maximize functionality
  const [maximizedChartId, setMaximizedChartId] = useState(null);
  const [loading, setLoading] = useState(false);

  // State for modal sort order
  const [modalSortOrder, setModalSortOrder] = useState('low-high');

  // State for modal equipment filter
  const [modalEquipmentFilter, setModalEquipmentFilter] = useState('all');

  // State for dashboard chart sort orders
  const [sortOrder, setSortOrder] = useState({});

  // State for Drill-down functionality
  const [selectedEquipmentForDrillDown, setSelectedEquipmentForDrillDown] = useState(null);
  const [selectedEquipmentIdForDrillDown, setSelectedEquipmentIdForDrillDown] = useState(null);
  const [selectedDriverForDrillDown, setSelectedDriverForDrillDown] = useState(null);
  const [selectedOperatorIdForDrillDown, setSelectedOperatorIdForDrillDown] = useState(null);

  // Refs to track previous drill-down selection to detect changes
  const prevSelectedEquipmentRef = useRef(null);
  const prevSelectedEquipmentIdRef = useRef(null);
  const prevSelectedDriverRef = useRef(null);
  const prevSelectedOperatorIdRef = useRef(null);

  // Track if Fuel component has loaded its initial data (LOCAL state, not global)
  const [fuelInitialDataLoaded, setFuelInitialDataLoaded] = useState(false);

  // Track previous manualTrigger to detect when arrow button is clicked
  const prevManualTriggerRef = useRef(manualTrigger);

  // Track previous activeTab to detect when Fuel tab becomes active
  const prevActiveTabRef = useRef(activeTab);

  // Track if API call is in progress to prevent duplicate calls
  const isFetchingRef = useRef(false);

  // State for summary metrics
  const [summary, setSummary] = useState({
    totalFuelQtyLitres: 0,
    totalTrips: 0,
    avgFuelConsumption: {
      litrePerHour: 0,
      kmPerLitre: 0
    },
    // Percentage changes
    fuelQtyChange: 0,
    tripsChange: 0
  });

  // Store previous period values for percentage calculation
  const [previousSummary, setPreviousSummary] = useState({
    totalFuelQtyLitres: 0,
    totalTrips: 0
  });

  // State for all chart data
  const [totalFuelByDate, setTotalFuelByDate] = useState([]);
  const [totalFuelPerHrByDate, setTotalFuelPerHrByDate] = useState([]);
  const [totalFuelByDriver, setTotalFuelByDriver] = useState([]);
  const [fuelPerHrByDriver, setFuelPerHrByDriver] = useState([]);
  const [fuelPerHourAndProductivity, setFuelPerHourAndProductivity] = useState([]);
  const [fuelPerKmAndFuelPerHr, setFuelPerKmAndFuelPerHr] = useState([]);
  const [fuelPerHrAndAvgFuelPerHrTrips, setFuelPerHrAndAvgFuelPerHrTrips] = useState([]);
  const [fuelPerHrAndAvgFuelPerHr, setFuelPerHrAndAvgFuelPerHr] = useState([]);
  const [fuelPerKmTotal, setFuelPerKmTotal] = useState([]);



  // Chart data object for compatibility with existing code
  const chartData = {
    totalFuelByDate,
    totalFuelPerHrByDate,
    totalFuelConsumptionByDriver: totalFuelByDriver,
    fuelConsumptionPerHrByDriver: fuelPerHrByDriver,
    fuelPerHourAndProductivity,
    fuelPerKmAndFuelPerHr,
    fuelPerHrAvgFuel: fuelPerHrAndAvgFuelPerHrTrips, // For stacked bar chart
    fuelPerHrAvgFuelComposed: fuelPerHrAndAvgFuelPerHr, // For composed chart
    fuelPerKmTotal
  };

  const renderDriverFuelTooltip = useCallback(({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    const point = payload[0]?.payload || {};
    const dataKey = payload[0]?.dataKey || '';
    const driverName = point.driverName || point.label || label || '-';
    const empCode = point.empCode || point.emp_code || '-';

    const isFuelPerHour = dataKey === 'fuelPerHr' || point.fuelPerHr !== undefined || point.fuelPerHour !== undefined;
    const metricColor = payload[0]?.color || payload[0]?.fill || (isFuelPerHour ? '#ec4899' : '#3b82f6');
    const rawValue = isFuelPerHour
      ? (point.fuelPerHr ?? point.fuelPerHour ?? payload[0]?.value ?? 0)
      : (point.totalFuel ?? ((point.consumption ?? payload[0]?.value ?? 0) * 1000));

    const formattedValue = Number(rawValue || 0).toLocaleString('en-IN', {
      maximumFractionDigits: 2
    });

    return (
      <div className="rounded-md border border-gray-200 bg-white px-3 py-2 shadow-md">
        <div className="text-xs text-gray-700"><span className="font-semibold">Emp Code:</span> {empCode}</div>
        <div className="text-xs text-gray-700"><span className="font-semibold">Driver Name:</span> {driverName}</div>
        <div className="text-xs">
          <span className="font-semibold" style={{ color: metricColor }}>{isFuelPerHour ? 'Fuel per Hr' : 'Total Fuel'}:</span>{' '}
          <span className="font-semibold" style={{ color: metricColor }}>{formattedValue}</span>
        </div>
      </div>
    );
  }, []);

  // Fetch fuel analytics data from API
  const fetchFuelData = useCallback(async () => {
    // Only fetch if Fuel is the active tab
    if (activeTab !== 'Fuel') {
      console.log('⏸️ Skipping Fuel API call - not active tab');
      return;
    }

    // Prevent duplicate calls
    if (isFetchingRef.current) {
      console.log('⏸️ API call already in progress, skipping duplicate request');
      return;
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);
      console.log('🔄 Fetching fuel analytics data...');

      // Get date range from filters or use default (this month start to today)
      const today = new Date();
      const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);

      const startDate = filters.dateRange?.from
        ? formatDateForApi(filters.dateRange.from)
        : formatDateForApi(thisMonthStart);
      const endDate = filters.dateRange?.to
        ? formatDateForApi(filters.dateRange.to)
        : formatDateForApi(today);

      // Build API filters - only include selected filters
      const apiFilters = {};

      // Add company ID if selected (required)
      if (filters.selectedCompany) {
        apiFilters.companyId = filters.selectedCompany;
      }

      // Add date range if both dates are selected
      if (startDate && endDate) {
        apiFilters.fromDate = startDate;
        apiFilters.toDate = endDate;
      }

      // Add optional filters ONLY if they are selected (not empty/null/undefined)
      if (filters.selectedLocation) {
        apiFilters.locationId = filters.selectedLocation;
        console.log('📍 Location filter:', filters.selectedLocation);
      }
      if (filters.selectedMachine) {
        apiFilters.equipmentId = filters.selectedMachine;
        console.log('🔧 Machine filter:', filters.selectedMachine, '(mapped to equipmentId)');
      }

      if (filters.selectedShift) {
        apiFilters.shiftNo = filters.selectedShift;
        console.log('⏰ Shift filter:', filters.selectedShift);
      }

      // Add drill-down filters
      if (selectedOperatorIdForDrillDown) {
        apiFilters.operatorId = selectedOperatorIdForDrillDown;
        console.log('👷 Operator drill-down filter:', selectedOperatorIdForDrillDown);
      }

      if (selectedEquipmentIdForDrillDown) {
        // Find equipment ID if possible, otherwise use name (API might handle it)
        // For now, let's assume if machine filter isn't set, we use drill-down equipment
        if (!apiFilters.equipmentId) {
          apiFilters.equipmentId = selectedEquipmentIdForDrillDown;
          console.log('🔧 Equipment drill-down filter (ID):', selectedEquipmentIdForDrillDown);
        }
      } else if (selectedEquipmentForDrillDown) {
        // Fallback to name if ID is missing (for legacy or display-only cases)
        if (!apiFilters.equipmentId) {
          apiFilters.equipmentId = selectedEquipmentForDrillDown;
          console.log('🔧 Equipment drill-down filter (Name):', selectedEquipmentForDrillDown);
        }
      }

      console.log('📤 Fuel API filters:', apiFilters);
      console.log('📋 All filters from context:', {
        selectedCompany: filters.selectedCompany,
        selectedLocation: filters.selectedLocation,
        selectedMachine: filters.selectedMachine,
        selectedShift: filters.selectedShift
      });

      const response = await fuelAnalyticsAPI.getAnalytics(apiFilters);

      if (response && response.success) {
        console.log('✅ Fuel analytics data received:', response);

        // Update summary data
        if (response.summary) {
          const currentFuelQty = response.summary.totalFuelQtyLitres || 0;
          const currentTrips = response.summary.totalTrips || 0;

          // Calculate percentage change by comparing first half vs second half of date range
          // This gives a trend indicator within the current period
          let fuelQtyChange = 0;
          let tripsChange = 0;

          // Handle both new API structure (object with current/forecast) and legacy (direct array)
          const fuelByDateRaw = response.graphs?.totalFuelByDate;
          const fuelByDateArray = fuelByDateRaw?.current && Array.isArray(fuelByDateRaw.current)
            ? fuelByDateRaw.current
            : Array.isArray(fuelByDateRaw) ? fuelByDateRaw : [];

          if (fuelByDateArray.length > 1) {
            const midPoint = Math.floor(fuelByDateArray.length / 2);
            const firstHalf = fuelByDateArray.slice(0, midPoint);
            const secondHalf = fuelByDateArray.slice(midPoint);

            const firstHalfAvg = firstHalf.reduce((sum, item) => sum + (item.totalFuel || 0), 0) / firstHalf.length;
            const secondHalfAvg = secondHalf.reduce((sum, item) => sum + (item.totalFuel || 0), 0) / secondHalf.length;

            if (firstHalfAvg > 0) {
              fuelQtyChange = ((secondHalfAvg - firstHalfAvg) / firstHalfAvg) * 100;
            }
          }

          // For trips, we can use a similar approach or compare with previous period
          // For now, use comparison with previous API call value
          const calculatePercentageChange = (current, previous) => {
            if (!previous || previous === 0) return fuelQtyChange; // Use fuel trend if no previous data
            return ((current - previous) / previous) * 100;
          };

          tripsChange = calculatePercentageChange(currentTrips, previousSummary.totalTrips);

          // If no previous trips data, use fuel trend as approximation
          if (previousSummary.totalTrips === 0) {
            tripsChange = fuelQtyChange;
          }

          // Update previous summary for next calculation
          setPreviousSummary({
            totalFuelQtyLitres: currentFuelQty,
            totalTrips: currentTrips
          });

          setSummary({
            totalFuelQtyLitres: currentFuelQty,
            totalTrips: currentTrips,
            avgFuelConsumption: {
              litrePerHour: response.summary.avgFuelConsumption?.litrePerHour || 0,
              kmPerLitre: response.summary.avgFuelConsumption?.kmPerLitre || 0
            },
            fuelQtyChange: fuelQtyChange,
            tripsChange: tripsChange
          });
        }

        // Transform and set chart data from graphs
        if (response.graphs) {
          const graphs = response.graphs;

          // Update available machines list for FilterBar machine dropdown
          // Use data from fuelPerHourAndProductivity or other equipment-based graphs
          const equipmentGraphs = [
            graphs.fuelPerHourAndProductivity,
            graphs.fuelPerKmAndFuelPerHr,
            graphs.fuelPerHrAndAvgFuelPerHrTrips,
            graphs.fuelPerHrAndAvgFuelPerHr,
            graphs.fuelPerKmTotal
          ];

          const machinesFromData = [];
          const seenMachineIds = new Set();

          equipmentGraphs.forEach(graph => {
            if (graph && Array.isArray(graph)) {
              graph.forEach(item => {
                if (item.equipmentId && !seenMachineIds.has(item.equipmentId)) {
                  seenMachineIds.add(item.equipmentId);
                  machinesFromData.push({
                    machineId: item.equipmentId,
                    machineName: item.equipmentNo || `Machine ${item.equipmentId}`
                  });
                }
              });
            }
          });

          if (machinesFromData.length > 0) {
            console.log('🔧 Updating available machines in context:', machinesFromData.length);
            setAvailableMachines(machinesFromData);
          }

          // Total Fuel by Date - Line chart
          if (graphs.totalFuelByDate) {
            let fuelByDateData = [];

            // Handle new API structure with current and forecast arrays
            if (graphs.totalFuelByDate.current && Array.isArray(graphs.totalFuelByDate.current)) {
              const currentData = graphs.totalFuelByDate.current;
              const forecastData = graphs.totalFuelByDate.forecast || [];

              // Create a map for forecast data by date
              const forecastMap = {};
              forecastData.forEach(item => {
                forecastMap[item.date] = item.totalFuel;
              });

              // Combine current and forecast data
              const allDates = new Set([
                ...currentData.map(item => item.date),
                ...forecastData.map(item => item.date)
              ]);

              fuelByDateData = Array.from(allDates).sort().map(date => {
                const currentItem = currentData.find(item => item.date === date);
                const forecastItem = forecastData.find(item => item.date === date);
                return {
                  date: formatDateForChart(date),
                  totalFuel: currentItem?.totalFuel || forecastItem?.totalFuel || 0,
                  current: currentItem?.totalFuel || null,
                  forecast: forecastItem?.totalFuel || null
                };
              });
            }
            // Handle legacy structure (direct array)
            else if (Array.isArray(graphs.totalFuelByDate)) {
              fuelByDateData = graphs.totalFuelByDate.map(item => ({
                date: formatDateForChart(item.date),
                totalFuel: item.totalFuel,
                current: item.totalFuel,
                forecast: item.totalFuel * 0.9
              }));
            }

            setTotalFuelByDate(fuelByDateData);
          }

          // Total Fuel Per Hr by Date - Line chart
          if (graphs.totalFuelPerHrByDate) {
            let fuelPerHrByDateData = [];

            // Handle new API structure with current and forecast arrays
            if (graphs.totalFuelPerHrByDate.current && Array.isArray(graphs.totalFuelPerHrByDate.current)) {
              const currentData = graphs.totalFuelPerHrByDate.current;
              const forecastData = graphs.totalFuelPerHrByDate.forecast || [];

              // Combine current and forecast data
              const allDates = new Set([
                ...currentData.map(item => item.date),
                ...forecastData.map(item => item.date)
              ]);

              fuelPerHrByDateData = Array.from(allDates).sort().map(date => {
                const currentItem = currentData.find(item => item.date === date);
                const forecastItem = forecastData.find(item => item.date === date);
                return {
                  date: formatDateForChart(date),
                  fuelPerHour: currentItem?.fuelPerHour || forecastItem?.fuelPerHour || 0,
                  current: currentItem?.fuelPerHour || null,
                  forecast: forecastItem?.fuelPerHour || null
                };
              });
            }
            // Handle legacy structure (direct array)
            else if (Array.isArray(graphs.totalFuelPerHrByDate)) {
              fuelPerHrByDateData = graphs.totalFuelPerHrByDate.map(item => ({
                date: formatDateForChart(item.date),
                fuelPerHour: item.fuelPerHour,
                current: item.fuelPerHour,
                forecast: item.fuelPerHour * 0.9
              }));
            }

            setTotalFuelPerHrByDate(fuelPerHrByDateData);
          }

          // Total Fuel by Driver - Horizontal Bar chart
          if (graphs.totalFuelByDriver && Array.isArray(graphs.totalFuelByDriver)) {
            const fuelByDriverData = graphs.totalFuelByDriver
              .map(item => ({
                label: item.driverName,
                driverName: item.driverName,
                operatorId: item.operatorId,
                empCode: item.emp_code,
                totalFuel: item.totalFuel,
                consumption: item.totalFuel / 1000 // Convert to thousands for display
              }));
            setTotalFuelByDriver(fuelByDriverData);
          }

          // Fuel Per Hr by Driver - Bar chart
          if (graphs.fuelPerHrByDriver && Array.isArray(graphs.fuelPerHrByDriver)) {
            const fuelPerHrByDriverData = graphs.fuelPerHrByDriver
              .map(item => ({
                displayName: item.driverName.split(' ')[0], // First name for cleaner display
                driverName: item.driverName, // Full name for uniqueness in chart keys
                operatorId: item.operatorId,
                empCode: item.emp_code,
                fuelPerHour: item.fuelPerHour,
                fuelPerHr: item.fuelPerHour
              }));
            setFuelPerHrByDriver(fuelPerHrByDriverData);
          }

          // Fuel Per Hour and Productivity - Area chart
          if (graphs.fuelPerHourAndProductivity && Array.isArray(graphs.fuelPerHourAndProductivity)) {
            const fuelProductivityData = graphs.fuelPerHourAndProductivity
              .map(item => ({
                name: item.equipmentNo,
                fuelPerHour: item.fuelPerHour,
                productivity: item.productivity
              }));
            setFuelPerHourAndProductivity(fuelProductivityData);
          }

          // Fuel Per Km and Fuel Per Hr - Area chart
          if (graphs.fuelPerKmAndFuelPerHr && Array.isArray(graphs.fuelPerKmAndFuelPerHr)) {
            const fuelPerKmHrData = graphs.fuelPerKmAndFuelPerHr
              .map(item => ({
                name: item.equipmentNo,
                fuelPerKm: item.fuelPerKm,
                fuelPerHour: item.fuelPerHour
              }));
            setFuelPerKmAndFuelPerHr(fuelPerKmHrData);
          }

          // Fuel Per Hr and Avg Fuel Per Hr with Trips - Stacked Bar chart
          if (graphs.fuelPerHrAndAvgFuelPerHrTrips && Array.isArray(graphs.fuelPerHrAndAvgFuelPerHrTrips)) {
            const fuelHrTripsData = graphs.fuelPerHrAndAvgFuelPerHrTrips
              .map(item => ({
                name: item.equipmentNo,
                equipmentId: item.equipmentId,
                totalTrips: item.totalTrips,
                totalFuel: item.fuelPerHour,
                avgFuelPerHr: item.avgFuelPerHour
              }));
            setFuelPerHrAndAvgFuelPerHrTrips(fuelHrTripsData);
          }

          // Fuel Per Hr and Avg Fuel Per Hr - Composed chart
          if (graphs.fuelPerHrAndAvgFuelPerHr && Array.isArray(graphs.fuelPerHrAndAvgFuelPerHr)) {
            const fuelHrAvgData = graphs.fuelPerHrAndAvgFuelPerHr
              .map(item => ({
                name: item.equipmentNo,
                equipmentId: item.equipmentId,
                fuelPerHr: item.fuelPerHour,
                avgFuelPerHr: item.avgFuelPerHour
              }));
            setFuelPerHrAndAvgFuelPerHr(fuelHrAvgData);
          }

          // Fuel Per Km Total - Bar chart
          if (graphs.fuelPerKmTotal && Array.isArray(graphs.fuelPerKmTotal)) {
            const fuelPerKmTotalData = graphs.fuelPerKmTotal
              .map(item => ({
                name: item.equipmentNo,
                equipmentId: item.equipmentId,
                fuelPerKmTotal: item.fuelPerKm
              }));
            setFuelPerKmTotal(fuelPerKmTotalData);
          }
        }

        // Mark initial data as loaded after first successful API call
        if (!fuelInitialDataLoaded) {
          setFuelInitialDataLoaded(true);
          prevManualTriggerRef.current = manualTrigger; // Initialize ref
          console.log('✅ Initial data loaded - subsequent filter changes require manual trigger');
        } else {
          // Update ref after successful manual trigger API call
          prevManualTriggerRef.current = manualTrigger;
        }

        console.log('✅ Fuel analytics data loaded successfully');
      } else {
        console.warn('⚠️ Fuel analytics API returned no data or error');
      }
    } catch (error) {
      console.error('❌ Error fetching fuel analytics:', error);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [filters, fuelInitialDataLoaded, manualTrigger, activeTab, selectedOperatorIdForDrillDown, selectedEquipmentForDrillDown, selectedEquipmentIdForDrillDown, setAvailableMachines]);

  // Fetch fuel data - auto-trigger on initial load, manual trigger after that
  useEffect(() => {
    // Only fetch if Fuel is the active tab
    if (activeTab !== 'Fuel') {
      console.log('⏸️ Skipping Fuel API call - not active tab');
      prevActiveTabRef.current = activeTab;
      return;
    }

    // Check if we just switched to Fuel tab (was not Fuel before)
    const justSwitchedToFuel = prevActiveTabRef.current !== 'Fuel' && activeTab === 'Fuel';
    prevActiveTabRef.current = activeTab; // Update ref

    if (justSwitchedToFuel) {
      console.log('🔄 Switched to Fuel tab - triggering API call');
      setFuelInitialDataLoaded(false);
      prevManualTriggerRef.current = manualTrigger; // Initialize ref

      // Only fetch if we have a company selected
      const companyId = filters.selectedCompany ||
        localStorage.getItem('companyId') ||
        sessionStorage.getItem('companyId');

      if (companyId) {
        fetchFuelData();
      } else {
        console.log('⏳ Waiting for company selection to fetch fuel data...');
      }
      return;
    }

    // Only fetch if we have a company selected
    const companyId = filters.selectedCompany ||
      localStorage.getItem('companyId') ||
      sessionStorage.getItem('companyId');

    if (!companyId) {
      console.log('⏳ Waiting for company selection to fetch fuel data...');
      return;
    }

    // On initial load (when Fuel tab is first accessed), automatically trigger API with default date range
    if (!fuelInitialDataLoaded) {
      console.log('🔄 Initial load - automatically fetching fuel data with default date range');
      prevManualTriggerRef.current = manualTrigger; // Initialize ref
      fetchFuelData();
      return;
    }

    // After initial load, trigger API if manual trigger activated OR drill-down selection changed
    if (fuelInitialDataLoaded) {
      const drillDownChanged =
        selectedOperatorIdForDrillDown !== prevSelectedOperatorIdRef.current ||
        selectedEquipmentIdForDrillDown !== prevSelectedEquipmentIdRef.current ||
        selectedEquipmentForDrillDown !== prevSelectedEquipmentRef.current;

      const manualTriggerActivated = manualTrigger !== prevManualTriggerRef.current;

      if (!drillDownChanged && !manualTriggerActivated) {
        console.log('⏸️ Skipping auto API call - waiting for manual trigger or drill-down');
        return;
      }

      // Update refs to current values
      prevManualTriggerRef.current = manualTrigger;
      prevSelectedOperatorIdRef.current = selectedOperatorIdForDrillDown;
      prevSelectedEquipmentIdRef.current = selectedEquipmentIdForDrillDown;
      prevSelectedEquipmentRef.current = selectedEquipmentForDrillDown;

      if (drillDownChanged) {
        console.log('🔍 Drill-down selection changed - automatically triggering API call');
      } else {
        console.log('🔵 Manual trigger activated - proceeding with API call');
      }

      fetchFuelData();
    }
  }, [
    filters.selectedCompany,
    filters.dateRange?.from?.getTime(), // Trigger when date range changes
    filters.dateRange?.to?.getTime(), // Trigger when date range changes
    filters.selectedMachine, // Include machine filter to ensure latest value is used
    filters.selectedLocation, // Include location filter to ensure latest value is used
    filters.selectedShift, // Include shift filter to ensure latest value is used
    selectedOperatorIdForDrillDown, // Trigger when driver drill-down changes
    selectedEquipmentIdForDrillDown, // Trigger when equipment drill-down changes
    selectedEquipmentForDrillDown, // Trigger when equipment drill-down changes
    manualTrigger, // Trigger refresh when user clicks the arrow button
    activeTab, // Only fetch when Fuel is the active tab
    // NOTE: fuelInitialDataLoaded intentionally NOT included to prevent double API calls
    fetchFuelData // Include fetchFuelData - isFetchingRef guard prevents duplicates
  ]);

  // Handler for equipment drill-down - click on equipment name in chart
  const handleEquipmentDrillDown = useCallback((equipmentName, equipmentId) => {
    console.log('🔍 Drill-down clicked for equipment:', equipmentName, 'ID:', equipmentId);
    setSelectedEquipmentForDrillDown(equipmentName);
    setSelectedEquipmentIdForDrillDown(equipmentId);
    // The useEffect will automatically trigger API call with selectedEquipment
  }, []);

  // Handler for driver drill-down - click on driver name in chart
  const handleDriverDrillDown = useCallback((driverName, operatorId) => {
    console.log('🔍 Drill-down clicked for driver:', driverName, 'OperatorId:', operatorId);
    setSelectedDriverForDrillDown(driverName);
    setSelectedOperatorIdForDrillDown(operatorId);
    // The useEffect will automatically trigger API call with operatorId
  }, []);

  // Handler to clear equipment drill-down
  const handleClearDrillDown = useCallback(() => {
    console.log('🔍 Clearing equipment drill-down');
    setSelectedEquipmentForDrillDown(null);
    setSelectedEquipmentIdForDrillDown(null);
    // The useEffect will automatically trigger API call without selectedEquipment
  }, []);

  // Handler to clear driver drill-down
  const handleClearDriverDrillDown = useCallback(() => {
    console.log('🔍 Clearing driver drill-down');
    setSelectedDriverForDrillDown(null);
    setSelectedOperatorIdForDrillDown(null);
    // The useEffect will automatically trigger API call without selectedDriver
  }, []);

  // Chart configurations - maps chart IDs to their properties
  // TODO: When API is ready, follow these steps:
  // 1. Uncomment the apiEndpoint property in each chart config below
  // 2. Update the apiEndpoint URLs to match your API routes
  // 3. Replace the handleMaximize function with the async version (see commented code below)
  // 4. Remove or comment out the mockData import if no longer needed
  const chartConfigs = {
    'total-fuel-by-date': {
      title: 'Total Fuel by Date',
      dataKey: 'totalFuelByDate',
      // apiEndpoint: '/api/fuel/total-fuel-by-date',
      chartType: 'line',
      chartProps: {
        margin: { top: 5, right: 20, left: 10, bottom: 20 },
        xAxis: {
          dataKey: 'date',
          tick: { fontSize: 12 },
          label: { value: 'Date', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: 12 } }
        },
        yAxis: {
          tick: { fontSize: 12 },
          label: { value: 'Total Fuel', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 12 } }
        },
        lines: [
          { dataKey: 'current', stroke: '#3b82f6', strokeWidth: 2, dot: { r: 4 }, name: 'Current' },
          { dataKey: 'forecast', stroke: '#f97316', strokeWidth: 2, strokeDasharray: '5 5', dot: { r: 4 }, name: 'Forecast' }
        ]
      }
    },
    'total-fuel-per-hr-by-date': {
      title: 'Total Fuel Per Hr by Date',
      dataKey: 'totalFuelPerHrByDate',
      // apiEndpoint: '/api/fuel/total-fuel-per-hr-by-date',
      chartType: 'line',
      chartProps: {
        margin: { top: 5, right: 20, left: 10, bottom: 20 },
        xAxis: {
          dataKey: 'date',
          tick: { fontSize: 12 },
          label: { value: 'Date', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: 12 } }
        },
        yAxis: {
          tick: { fontSize: 12 },
          label: { value: 'Fuel per Hr', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 12 } }
        },
        lines: [
          { dataKey: 'current', stroke: '#3b82f6', strokeWidth: 2, dot: { r: 4 }, name: 'Current' },
          { dataKey: 'forecast', stroke: '#f97316', strokeWidth: 2, strokeDasharray: '5 5', dot: { r: 4 }, name: 'Forecast' }
        ]
      }
    },
    'total-fuel-consumption-by-driver': {
      title: 'Total Fuel Consumption by Driver Name',
      dataKey: 'totalFuelConsumptionByDriver',
      // apiEndpoint: '/api/fuel/total-fuel-consumption-by-driver',
      chartType: 'horizontalBar',
      chartProps: {
        layout: 'vertical',
        margin: { top: 5, right: 30, left: 10, bottom: 20 },
        xAxis: {
          type: 'number',
          tick: { fontSize: 12 },
          tickFormatter: (value) => `${value}k`,
          label: { value: 'Consumption', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: 12 } }
        },
        yAxis: {
          type: 'category',
          dataKey: 'label',
          tick: { fontSize: 11 },
          interval: 0,
          width: 150,
          label: { value: 'Driver Name', angle: -90, position: 'insideLeft', offset: 0, style: { textAnchor: 'middle', fontSize: 12 } }
        },
        dataKey: 'consumption',
        fill: '#3b82f6',
        radius: [0, 4, 4, 0]
      }
    },
    'fuel-consumption-per-hr-by-driver': {
      title: 'Fuel consumption per hr by Driver Name',
      dataKey: 'fuelConsumptionPerHrByDriver',
      // apiEndpoint: '/api/fuel/fuel-consumption-per-hr-by-driver',
      chartType: 'bar',
      chartProps: {
        barCategoryGap: '10%',
        dataKey: 'fuelPerHr',
        fill: '#ec4899',
        radius: [4, 4, 0, 0],
        barSize: 34.94,
        xAxis: {
          dataKey: 'driverName',
          tick: { fontSize: 12 },
          angle: 0,
          textAnchor: 'middle',
          height: 60,
          interval: 0,
          label: { value: 'Driver Name', position: 'insideBottom', offset: -10, style: { textAnchor: 'middle', fontSize: 12 } }
        },
        yAxis: {
          tick: { fontSize: 12 },
          domain: [0, 10],
          label: { value: 'Fuel per Hr', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }
        }
      }
    },
    'fuel-per-hour-and-productivity': {
      title: 'Fuel per Hour and Productivity',
      dataKey: 'fuelPerHourAndProductivity',
      // apiEndpoint: '/api/fuel/fuel-per-hour-and-productivity',
      chartType: 'area',
      chartProps: {
        margin: { top: 5, right: 50, left: 10, bottom: 20 },
        xAxis: { dataKey: 'name', tick: { fontSize: 11 } },
        yAxes: [
          {
            yAxisId: 'left',
            tick: { fontSize: 11 },
            domain: [9.0, 9.4],
            label: { value: 'Fuel per Hour', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } }
          },
          {
            yAxisId: 'right',
            orientation: 'right',
            tick: { fontSize: 11 },
            domain: [440000, 460000],
            tickFormatter: (value) => `${value / 1000}K`,
            label: { value: 'Productivity', angle: 90, position: 'insideRight', style: { textAnchor: 'middle', fontSize: 11 } }
          }
        ],
        areas: [
          {
            yAxisId: 'left',
            dataKey: 'fuelPerHour',
            stroke: '#a855f7',
            strokeWidth: 2,
            fill: 'url(#colorFuel)',
            gradientId: 'colorFuel',
            gradientStops: [
              { offset: '5%', stopColor: '#a855f7', stopOpacity: 0.6 },
              { offset: '95%', stopColor: '#a855f7', stopOpacity: 0.1 }
            ]
          },
          {
            yAxisId: 'right',
            dataKey: 'productivity',
            stroke: '#fbbf24',
            strokeWidth: 2,
            fill: 'url(#colorProductivity)',
            gradientId: 'colorProductivity',
            gradientStops: [
              { offset: '5%', stopColor: '#fbbf24', stopOpacity: 0.6 },
              { offset: '95%', stopColor: '#fbbf24', stopOpacity: 0.1 }
            ]
          }
        ]
      }
    },
    'fuel-per-km-and-fuel-per-hr': {
      title: 'Fuel per Km and Fuel Per Hr',
      dataKey: 'fuelPerKmAndFuelPerHr',
      // apiEndpoint: '/api/fuel/fuel-per-km-and-fuel-per-hr',
      chartType: 'area',
      chartProps: {
        margin: { top: 5, right: 50, left: 10, bottom: 20 },
        xAxis: { dataKey: 'name', tick: { fontSize: 11 } },
        yAxes: [
          {
            yAxisId: 'left',
            tick: { fontSize: 11 },
            domain: [0.88, 0.90],
            label: { value: 'Fuel Per Km', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } }
          },
          {
            yAxisId: 'right',
            orientation: 'right',
            tick: { fontSize: 11 },
            domain: [9.0, 9.4],
            label: { value: 'Fuel per Hour', angle: 90, position: 'insideRight', style: { textAnchor: 'middle', fontSize: 11 } }
          }
        ],
        areas: [
          {
            yAxisId: 'left',
            dataKey: 'fuelPerKm',
            stroke: '#a855f7',
            strokeWidth: 2,
            fill: 'url(#colorFuelKm)',
            gradientId: 'colorFuelKm',
            gradientStops: [
              { offset: '5%', stopColor: '#a855f7', stopOpacity: 0.6 },
              { offset: '95%', stopColor: '#a855f7', stopOpacity: 0.1 }
            ]
          }
        ]
      }
    },
    'fuel-per-hr-avg-fuel-stacked': {
      title: 'Fuel per hr and Avg fuel per hour',
      dataKey: 'fuelPerHrAvgFuel',
      // apiEndpoint: '/api/fuel/fuel-per-hr-avg-fuel-stacked',
      chartType: 'stackedBar',
      chartProps: {
        margin: { top: 5, right: 20, left: 10, bottom: 20 },
        xAxis: { dataKey: 'name', tick: { fontSize: 11 }, label: { value: 'Equipment', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: 12 } } },
        yAxis: {
          tick: { fontSize: 11 },
          tickFormatter: (value) => value >= 1000 ? `${(value / 1000).toFixed(1)}K` : value,
          label: { value: 'Fuel per Hr', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } }
        },
        legend: {
          verticalAlign: 'top',
          height: 36,
          iconType: 'circle',
          formatter: (value) => {
            if (value === 'totalTrips') return 'Total Trips';
            if (value === 'totalFuel') return 'Total Fuel';
            return value;
          }
        },
        bars: [
          { dataKey: 'totalTrips', stackId: 'a', fill: '#60a5fa', radius: [0, 0, 0, 0] },
          { dataKey: 'totalFuel', stackId: 'a', fill: '#ef4444', radius: [4, 4, 0, 0] }
        ]
      }
    },
    'fuel-per-hr-avg-fuel-composed': {
      title: 'Fuel per hr and Avg fuel per hour',
      dataKey: 'fuelPerHrAvgFuelComposed',
      // apiEndpoint: '/api/fuel/fuel-per-hr-avg-fuel-composed',
      chartType: 'composed',
      chartProps: {
        margin: { top: 5, right: 20, left: 10, bottom: 20 },
        xAxis: { dataKey: 'name', tick: { fontSize: 11 }, label: { value: 'Equipment', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: 12 } } },
        yAxis: {
          tick: { fontSize: 11 },
          tickFormatter: (value) => value >= 1000 ? `${(value / 1000).toFixed(1)}K` : value,
          label: { value: 'Fuel per Hr', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } }
        },
        legend: {
          verticalAlign: 'top',
          height: 36,
          iconType: 'circle',
          formatter: (value) => {
            if (value === 'fuelPerHr') return 'Fuel per hr';
            if (value === 'avgFuelPerHr') return 'Avg fuel per hr';
            return value;
          }
        },
        bars: [
          { dataKey: 'fuelPerHr', fill: '#60a5fa', radius: [4, 4, 0, 0], barSize: 34.94, barCategoryGap: '10%' }
        ],
        lines: [
          { dataKey: 'avgFuelPerHr', stroke: '#ef4444', strokeWidth: 2, dot: false }
        ]
      }
    },
    'fuel-per-km-total': {
      title: 'Fuel per km_total',
      dataKey: 'fuelPerKmTotal',
      // apiEndpoint: '/api/fuel/fuel-per-km-total',
      chartType: 'bar',
      chartProps: {
        barCategoryGap: '10%',
        dataKey: 'fuelPerKmTotal',
        fill: '#a855f7',
        radius: [4, 4, 0, 0],
        barSize: 34.94,
        xAxis: { dataKey: 'name', tick: { fontSize: 12 }, label: { value: 'Equipment', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: 12 } } },
        yAxis: {
          tick: { fontSize: 12 },
          label: { value: 'Fuel per km_total', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 12 } }
        }
      }
    }
  };

  // Handler for maximize button - ready for API integration
  // TODO: Replace mockData access with API call when ready
  // Example API integration:
  // const handleMaximize = useCallback(async (chartId) => {
  //   const config = chartConfigs[chartId];
  //   if (!config) return;
  //
  //   setLoading(true);
  //   try {
  //     const response = await fetch(config.apiEndpoint, {
  //       method: 'GET',
  //       headers: { 'Content-Type': 'application/json' },
  //       // Add any filters/params: { limit: null, sort: 'low-high' }
  //     });
  //     const data = await response.json();
  //     setChartData(prev => ({
  //       ...prev,
  //       [config.dataKey]: data.result || data
  //     }));
  //     setMaximizedChartId(chartId);
  //   } catch (error) {
  //     console.error('Error fetching chart data:', error);
  //     // Fallback to mock data on error
  //     setMaximizedChartId(chartId);
  //   } finally {
  //     setLoading(false);
  //   }
  // }, []);
  const handleMaximize = useCallback((chartId) => {
    const config = chartConfigs[chartId];
    if (!config) return;

    // Currently using mockData - will be replaced with API call
    setMaximizedChartId(chartId);
  }, []);

  const handleCloseModal = useCallback(() => {
    setMaximizedChartId(null);
    setModalSortOrder('low-high'); // Reset sort order when closing modal
    setModalEquipmentFilter('all'); // Reset equipment filter when closing modal
  }, []);

  // Get unique equipment/driver list for the current chart
  const getEquipmentListForChart = useCallback((chartId) => {
    let dataSource = [];
    let nameField = 'name';

    if (chartId === 'fuel-per-hour-and-productivity') {
      dataSource = fuelPerHourAndProductivity || [];
    } else if (chartId === 'fuel-per-km-and-fuel-per-hr') {
      dataSource = fuelPerKmAndFuelPerHr || [];
    } else if (chartId === 'fuel-per-hr-avg-fuel-stacked') {
      dataSource = fuelPerHrAndAvgFuelPerHrTrips || [];
    } else if (chartId === 'fuel-per-hr-avg-fuel-composed') {
      dataSource = fuelPerHrAndAvgFuelPerHr || [];
    } else if (chartId === 'fuel-per-km-total') {
      dataSource = fuelPerKmTotal || [];
    } else if (chartId === 'total-fuel-consumption-by-driver') {
      dataSource = totalFuelByDriver || [];
      nameField = 'label';
    } else if (chartId === 'fuel-consumption-per-hr-by-driver') {
      dataSource = fuelPerHrByDriver || [];
      nameField = 'driverName';
    }

    return [...new Set(dataSource.map(item => item[nameField]).filter(Boolean))];
  }, [fuelPerHourAndProductivity, fuelPerKmAndFuelPerHr, fuelPerHrAndAvgFuelPerHrTrips, fuelPerHrAndAvgFuelPerHr, fuelPerKmTotal, totalFuelByDriver, fuelPerHrByDriver]);

  // Helper function to sort chart data based on sort order
  const getSortedData = useCallback((data, sortOrder, sortKey) => {
    if (!data || data.length === 0) return data;

    const sorted = [...data].sort((a, b) => {
      const valueA = a[sortKey] || 0;
      const valueB = b[sortKey] || 0;

      if (sortOrder === 'low-high') {
        return valueA - valueB;
      } else {
        return valueB - valueA;
      }
    });

    return sorted;
  }, []);

  // Render maximized chart component
  const renderMaximizedChart = (chartId) => {
    const config = chartConfigs[chartId];
    if (!config) return null;

    let rawData = chartData[config.dataKey] || [];

    console.log(`📊 Rendering maximized chart: ${chartId}`, {
      dataKey: config.dataKey,
      rawDataLength: rawData.length,
      rawData: rawData,
      chartType: config.chartType
    });

    // Apply equipment filter if not "all"
    if (modalEquipmentFilter !== 'all') {
      // Determine the name field based on chart type
      let nameField = 'name';
      if (chartId === 'total-fuel-consumption-by-driver') {
        nameField = 'label';
      } else if (chartId === 'fuel-consumption-per-hr-by-driver') {
        nameField = 'driverName';
      }

      rawData = rawData.filter(item => item[nameField] === modalEquipmentFilter);
    }

    // Determine the sort key based on chart type and config
    const getSortKey = () => {
      if (config.chartType === 'bar') return config.chartProps?.dataKey || 'value';
      if (config.chartType === 'horizontalBar') return config.chartProps?.dataKey || 'consumption';
      if (config.chartType === 'line') return 'current';
      if (config.chartType === 'area') return config.chartProps?.areas?.[0]?.dataKey || 'value';
      if (config.chartType === 'stackedBar') return config.chartProps?.bars?.[0]?.dataKey || 'totalTrips';
      if (config.chartType === 'composed') return config.chartProps?.bars?.[0]?.dataKey || 'fuelPerHr';
      return 'value';
    };

    // Sort the data based on modal sort order
    const data = getSortedData(rawData, modalSortOrder, getSortKey());

    console.log(`📊 Sorted data for ${chartId}:`, data);

    if (config.chartType === 'bar') {
      const props = config.chartProps;
      // Calculate minimum width based on number of bars
      let minChartWidth;
      if (chartId === 'fuel-consumption-per-hr-by-driver') {
        // Dynamic width for driver chart to accommodate full names
        const maxLabelLength = Math.max(...data.map(item => (item.driverName || '').length), 10);
        const widthPerBar = Math.max(100, maxLabelLength * 10); // ~10px per char
        minChartWidth = Math.max(1000, data.length * widthPerBar);
      } else {
        const estimatedWidthPerBar = 100; // Approximate space per bar including gap
        minChartWidth = Math.max(1000, data.length * estimatedWidthPerBar);
      }

      // Increase gap between bars in maximized view for better readability
      const maximizedBarCategoryGap = '25%';
      // Set bar width slightly smaller for maximized view to look less "oversized"
      const maximizedBarSize = 48;

      // Ensure margin includes left space for Y-axis labels
      const chartMargin = props.margin || {};
      const defaultMargin = {
        top: chartMargin.top ?? 5,
        right: chartMargin.right ?? 10,
        left: chartMargin.left ?? (props.yAxis?.label ? 50 : 10),
        bottom: chartMargin.bottom ?? 30
      };

      // Create modified axis props without labels (we'll render them separately as fixed overlays)
      const xAxisPropsWithoutLabel = { ...props.xAxis };
      delete xAxisPropsWithoutLabel.label;

      const yAxisPropsWithoutLabel = { ...props.yAxis };
      delete yAxisPropsWithoutLabel.label;

      return (
        <div className="w-full h-full relative flex flex-col" key={`chart-container-${chartId}-${modalSortOrder}`}>
          {/* Fixed Y-axis label - positioned absolutely on the left */}
          {props.yAxis?.label && (
            <div
              className="absolute left-0 top-0 bottom-0 z-30 pointer-events-none flex items-center"
              style={{
                width: '60px',
                paddingLeft: '15px'
              }}
            >
              <div
                style={{
                  writingMode: 'vertical-rl',
                  textOrientation: 'mixed',
                  transform: 'rotate(180deg)',
                  fontSize: '13px', // Increased from 11px
                  fontWeight: 600,
                  color: '#374151',
                  whiteSpace: 'nowrap'
                }}
              >
                {props.yAxis.label.value || 'Y-Axis'}
              </div>
            </div>
          )}

          {/* Scrollable chart container */}
          <div
            className="flex-1 overflow-x-auto overflow-y-hidden relative"
            style={{ marginLeft: props.yAxis?.label ? '60px' : '0', marginBottom: props.xAxis?.label ? '30px' : '0' }}
          >
            <div style={{ minWidth: `${minChartWidth}px`, width: '100%', height: '100%', position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%" key={`chart-${chartId}-${modalSortOrder}`}>
                <BarChart data={data} barCategoryGap={maximizedBarCategoryGap} margin={defaultMargin}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis {...xAxisPropsWithoutLabel} />
                  <YAxis {...yAxisPropsWithoutLabel} />
                  <Tooltip cursor={false} content={renderDriverFuelTooltip} />
                  {props.referenceLine && (
                    <ReferenceLine
                      y={props.referenceLine.y}
                      stroke={props.referenceLine.stroke}
                      strokeDasharray={props.referenceLine.strokeDasharray}
                      strokeWidth={props.referenceLine.strokeWidth}
                    />
                  )}
                  <Bar
                    dataKey={props.dataKey}
                    fill={props.fill}
                    radius={props.radius}
                    barSize={maximizedBarSize}
                    activeBar={false}
                    onClick={(data, index, e) => {
                      if (e && e.stopPropagation) e.stopPropagation();
                      if (chartId === 'fuel-consumption-per-hr-by-driver' && data && data.driverName) {
                        handleDriverDrillDown(data.driverName, data.operatorId);
                      } else if (chartId === 'fuel-per-km-total' && data && data.name) {
                        handleEquipmentDrillDown(data.name, data.equipmentId);
                      }
                    }}
                    cursor="pointer"
                  >
                    {data.map((entry, index) => {
                      let fill = props.fill;
                      if (chartId === 'fuel-consumption-per-hr-by-driver') {
                        fill = entry.operatorId === selectedOperatorIdForDrillDown ? "#be185d" : props.fill;
                      } else if (chartId === 'fuel-per-km-total') {
                        fill = entry.equipmentId === selectedEquipmentIdForDrillDown || entry.name === selectedEquipmentForDrillDown ? "#7e22ce" : props.fill;
                      }
                      return <Cell key={`cell-${index}`} fill={fill} style={{ cursor: 'pointer' }} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Fixed X-axis label - positioned absolutely at the bottom */}
          {props.xAxis?.label && (
            <div
              className="absolute bottom-0 left-0 right-0 z-30 pointer-events-none flex justify-center items-center"
              style={{
                height: '30px',
                marginLeft: props.yAxis?.label ? '60px' : '0',
                fontSize: '14px', // Increased from 12px
                fontWeight: 600,
                color: '#374151'
              }}
            >
              {props.xAxis.label.value || 'X-Axis'}
            </div>
          )}
        </div>
      );
    } else if (config.chartType === 'horizontalBar') {
      const props = config.chartProps;
      // Increased height per item ensures bars (62.29px) have enough space (100px per item)
      const totalHeight = data.length > 5 ? Math.max(600, data.length * 100) : '100%';
      const xAxisHeight = 60;
      const yAxisWidth = props.yAxis?.label ? 180 : 150;

      // Extract axis labels
      const xAxisPropsWithoutLabel = { ...props.xAxis };
      delete xAxisPropsWithoutLabel.label;
      const yAxisPropsWithoutLabel = { ...props.yAxis };
      delete yAxisPropsWithoutLabel.label;

      return (
        <div className="w-full h-full flex flex-col overflow-hidden relative">
          {/* Fixed Y-axis label - positioned absolutely on the left */}
          {props.yAxis?.label && (
            <div
              className="absolute left-0 top-0 bottom-0 z-30 pointer-events-none flex items-center"
              style={{
                width: '60px',
                paddingLeft: '15px',
                paddingBottom: `${xAxisHeight}px`
              }}
            >
              <div
                style={{
                  writingMode: 'vertical-rl',
                  textOrientation: 'mixed',
                  transform: 'rotate(180deg)',
                  fontSize: props.yAxis.label.style?.fontSize || '14px', // Increased from 12px
                  fontWeight: 500,
                  color: '#374151',
                  whiteSpace: 'nowrap'
                }}
              >
                {props.yAxis.label.value || 'Y-Axis'}
              </div>
            </div>
          )}

          {/* Scrollable Data Area */}
          <div
            className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar"
            style={{ marginLeft: props.yAxis?.label ? '60px' : '0', marginBottom: `${xAxisHeight}px` }}
          >
            <div style={{ height: totalHeight, width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data}
                  layout="vertical"
                  margin={{ ...props.margin, bottom: 0, left: 0 }}
                  barCategoryGap="20%"
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                  <YAxis {...yAxisPropsWithoutLabel} width={yAxisWidth} />
                  {/* Hidden XAxis so the Bar chart knows the numerical scale */}
                  <XAxis {...xAxisPropsWithoutLabel} hide />
                  <Tooltip cursor={false} content={renderDriverFuelTooltip} />
                  <Bar
                    dataKey={props.dataKey}
                    fill={props.fill}
                    radius={props.radius}
                    barSize={62.29}
                    activeBar={false}
                    onClick={(data, index, e) => {
                      if (e && e.stopPropagation) e.stopPropagation();
                      if (chartId === 'total-fuel-consumption-by-driver' && data && data.label) {
                        handleDriverDrillDown(data.label, data.operatorId);
                      }
                    }}
                    cursor="pointer"
                  >
                    {data.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.operatorId === selectedOperatorIdForDrillDown ? "#1e40af" : props.fill}
                        style={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Sticky X-Axis with label */}
          <div
            className="w-full flex-shrink-0 relative"
            style={{
              height: `${xAxisHeight}px`,
              marginLeft: props.yAxis?.label ? '60px' : '0'
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart layout="vertical" data={data} margin={{ ...props.margin, top: 0, left: 0 }}>
                <XAxis {...xAxisPropsWithoutLabel} orientation="bottom" height={xAxisHeight - 5} />
                {/* Hidden YAxis to ensure XAxis alignment matches the data chart */}
                <YAxis {...yAxisPropsWithoutLabel} width={yAxisWidth} hide />
                {/* Hidden Bar ensures XAxis scales correctly for the labels */}
                <Bar dataKey={props.dataKey} hide />
              </BarChart>
            </ResponsiveContainer>
            {/* Fixed X-axis label */}
            {props.xAxis?.label && (
              <div
                className="absolute bottom-0 left-0 right-0 flex items-center justify-center"
                style={{ height: '20px' }}
              >
                <div
                  style={{
                    fontSize: props.xAxis.label.style?.fontSize || '12px',
                    fontWeight: 500,
                    color: '#374151',
                    textAlign: 'center'
                  }}
                >
                  {props.xAxis.label.value || 'X-Axis'}
                </div>
              </div>
            )}
          </div>
        </div>
      );
    } else if (config.chartType === 'line') {
      const props = config.chartProps;
      // Determine colors for indicators based on chart type
      const currentColor = props.lines?.[0]?.stroke || '#3b82f6';
      const forecastColor = props.lines?.[1]?.stroke || '#f97316';

      // Ensure margin includes left space for Y-axis labels
      const chartMargin = props.margin || {};
      const defaultMargin = {
        top: chartMargin.top ?? 5,
        right: chartMargin.right ?? 20,
        left: chartMargin.left ?? (props.yAxis?.label ? 50 : 10),
        bottom: chartMargin.bottom ?? 20
      };

      // Create modified axis props without labels (we'll render them separately as fixed overlays)
      const xAxisPropsWithoutLabel = { ...props.xAxis };
      delete xAxisPropsWithoutLabel.label;

      const yAxisPropsWithoutLabel = { ...props.yAxis };
      delete yAxisPropsWithoutLabel.label;

      return (
        <div className="w-full h-full relative flex flex-col">
          {/* Fixed Y-axis label - positioned absolutely on the left */}
          {props.yAxis?.label && (
            <div
              className="absolute left-0 top-0 bottom-0 z-30 pointer-events-none flex items-center"
              style={{
                width: '60px',
                paddingLeft: '15px',
                paddingBottom: '40px'
              }}
            >
              <div
                style={{
                  writingMode: 'vertical-rl',
                  textOrientation: 'mixed',
                  transform: 'rotate(180deg)',
                  fontSize: '13px', // Increased from 11px
                  fontWeight: 600,
                  color: '#374151',
                  whiteSpace: 'nowrap'
                }}
              >
                {props.yAxis.label.value || 'Y-Axis'}
              </div>
            </div>
          )}

          {/* Scrollable chart container */}
          <div
            className="flex-1 overflow-x-auto overflow-y-hidden relative"
            style={{ marginLeft: props.yAxis?.label ? '60px' : '0', marginBottom: props.xAxis?.label ? '60px' : '40px' }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={defaultMargin}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis {...xAxisPropsWithoutLabel} />
                <YAxis {...yAxisPropsWithoutLabel} />
                <Tooltip />
                {props.lines.map((line, index) => (
                  <Line
                    key={index}
                    type="monotone"
                    dataKey={line.dataKey}
                    stroke={line.stroke}
                    strokeWidth={line.strokeWidth}
                    strokeDasharray={line.strokeDasharray}
                    dot={line.dot}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Fixed X-axis label - positioned first, below chart */}
          {props.xAxis?.label && (
            <div
              className="absolute bottom-[40px] left-0 right-0 z-30 pointer-events-none flex justify-center items-center"
              style={{
                height: '30px',
                marginLeft: props.yAxis?.label ? '60px' : '0',
                fontSize: '14px', // Increased from 12px
                fontWeight: 600,
                color: '#374151'
              }}
            >
              {props.xAxis.label.value || 'X-Axis'}
            </div>
          )}

          {/* Legend - positioned below X-axis label */}
          <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-6 px-6 pb-4" style={{ marginLeft: props.yAxis?.label ? '60px' : '0' }}>
            <div className="flex items-center text-xs text-gray-600">
              <span className="w-3 h-3 rounded mr-2 inline-block" style={{ backgroundColor: currentColor }}></span>
              Current
            </div>
            <div className="flex items-center text-xs text-gray-600">
              <span className="w-3 h-3 rounded mr-2 inline-block" style={{ backgroundColor: forecastColor }}></span>
              Forecast
            </div>
          </div>
        </div>
      );
    } else if (config.chartType === 'area') {
      const props = config.chartProps;
      // Ensure margin includes left space for Y-axis labels
      const chartMargin = props.margin || {};
      const defaultMargin = {
        top: chartMargin.top ?? 5,
        right: chartMargin.right ?? 20,
        left: chartMargin.left ?? 50,
        bottom: chartMargin.bottom ?? 30
      };

      // Extract axis labels
      const xAxisPropsWithoutLabel = { ...props.xAxis };
      delete xAxisPropsWithoutLabel.label;

      return (
        <div className="w-full h-full relative flex flex-col">
          {/* Fixed Y-axis label - positioned absolutely on the left */}
          {props.yAxes && props.yAxes[0]?.label && (
            <div
              className="absolute left-0 top-0 bottom-0 z-30 pointer-events-none flex items-center"
              style={{
                width: '60px',
                paddingLeft: '15px'
              }}
            >
              <div
                style={{
                  writingMode: 'vertical-rl',
                  textOrientation: 'mixed',
                  transform: 'rotate(180deg)',
                  fontSize: props.yAxes[0].label.style?.fontSize || '11px',
                  fontWeight: 500,
                  color: '#374151',
                  whiteSpace: 'nowrap'
                }}
              >
                {props.yAxes[0].label.value || 'Y-Axis'}
              </div>
            </div>
          )}

          {/* Scrollable chart container */}
          <div
            className="flex-1 overflow-x-auto overflow-y-hidden relative"
            style={{ marginLeft: (props.yAxes && props.yAxes[0]?.label) ? '60px' : '0', marginBottom: props.xAxis?.label ? '30px' : '0' }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={defaultMargin}>
                <defs>
                  {props.areas.map((area, index) => (
                    <linearGradient key={index} id={area.gradientId} x1="0" y1="0" x2="0" y2="1">
                      {area.gradientStops.map((stop, stopIndex) => (
                        <stop key={stopIndex} offset={stop.offset} stopColor={stop.stopColor} stopOpacity={stop.stopOpacity} />
                      ))}
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis {...xAxisPropsWithoutLabel} />
                {props.yAxes.map((yAxis, index) => (
                  <YAxis key={index} yAxisId={yAxis.yAxisId} {...yAxis} />
                ))}
                <Tooltip />
                {props.areas.map((area, index) => (
                  <Area
                    key={index}
                    yAxisId={area.yAxisId}
                    type="linear"
                    dataKey={area.dataKey}
                    stroke={area.stroke}
                    strokeWidth={area.strokeWidth}
                    fill={area.fill}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Fixed X-axis label - positioned absolutely at the bottom */}
          {props.xAxis?.label && (
            <div
              className="absolute bottom-0 left-0 right-0 z-30 pointer-events-none flex justify-center items-center"
              style={{
                height: '30px',
                marginLeft: (props.yAxes && props.yAxes[0]?.label) ? '60px' : '0',
                fontSize: props.xAxis.label.style?.fontSize || '12px',
                fontWeight: 500,
                color: '#374151'
              }}
            >
              {props.xAxis.label.value || 'X-Axis'}
            </div>
          )}
        </div>
      );
    } else if (config.chartType === 'stackedBar') {
      const props = config.chartProps;
      // Calculate minimum width based on number of bars
      const barWidth = 62.29;
      const estimatedWidthPerBar = 100; // Approximate space per bar including gap
      const minChartWidth = Math.max(1000, data.length * estimatedWidthPerBar);

      // Increase gap between bars in maximized view for better readability
      const maximizedBarCategoryGap = '25%';
      // Set bar width slightly smaller for maximized view to look less "oversized"
      const maximizedBarSize = 48;

      // Ensure margin includes left space for Y-axis labels
      const chartMargin = props.margin || {};
      const defaultMargin = {
        top: chartMargin.top ?? 5,
        right: chartMargin.right ?? 10,
        left: chartMargin.left ?? (props.yAxis?.label ? 50 : 10),
        bottom: chartMargin.bottom ?? 30
      };

      // Create modified axis props without labels (we'll render them separately as fixed overlays)
      const xAxisPropsWithoutLabel = { ...props.xAxis };
      delete xAxisPropsWithoutLabel.label;

      const yAxisPropsWithoutLabel = { ...props.yAxis };
      delete yAxisPropsWithoutLabel.label;

      return (
        <div className="w-full h-full relative flex flex-col" key={`chart-container-${chartId}-${modalSortOrder}`}>
          {/* Fixed Y-axis label - positioned absolutely on the left */}
          {props.yAxis?.label && (
            <div
              className="absolute left-0 top-0 bottom-0 z-30 pointer-events-none flex items-center"
              style={{
                width: '60px',
                paddingLeft: '15px'
              }}
            >
              <div
                style={{
                  writingMode: 'vertical-rl',
                  textOrientation: 'mixed',
                  transform: 'rotate(180deg)',
                  fontSize: '13px', // Increased from 11px
                  fontWeight: 600,
                  color: '#374151',
                  whiteSpace: 'nowrap'
                }}
              >
                {props.yAxis.label.value || 'Y-Axis'}
              </div>
            </div>
          )}

          {/* Scrollable chart container */}
          <div
            className="flex-1 overflow-x-auto overflow-y-hidden relative"
            style={{ marginLeft: props.yAxis?.label ? '60px' : '0', marginBottom: (props.xAxis?.label || chartId === 'fuel-per-hr-avg-fuel-stacked') ? '70px' : '40px' }}
          >
            <div style={{ minWidth: `${minChartWidth}px`, width: '100%', height: '100%', position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%" key={`chart-${chartId}-${modalSortOrder}`}>
                <BarChart data={data} barCategoryGap={maximizedBarCategoryGap} margin={defaultMargin}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis {...xAxisPropsWithoutLabel} />
                  <YAxis {...yAxisPropsWithoutLabel} />
                  <Tooltip cursor={false} />
                  {props.referenceLine && (
                    <ReferenceLine
                      y={props.referenceLine.y}
                      yAxisId={props.referenceLine.yAxisId}
                      stroke={props.referenceLine.stroke}
                      strokeWidth={props.referenceLine.strokeWidth}
                    />
                  )}
                  {props.bars.map((bar, index) => (
                    <Bar
                      key={index}
                      dataKey={bar.dataKey}
                      stackId={bar.stackId}
                      fill={bar.fill}
                      radius={bar.radius}
                      barSize={maximizedBarSize}
                      activeBar={false}
                      onClick={(data, index, e) => {
                        if (e && e.stopPropagation) e.stopPropagation();
                        if (chartId === 'fuel-per-hr-avg-fuel-stacked' && data && data.name) {
                          handleEquipmentDrillDown(data.name, data.equipmentId);
                        }
                      }}
                      cursor="pointer"
                    >
                      {data.map((entry, i) => {
                        // Determine highlight color based on bar.dataKey (trips vs fuel)
                        let highlightColor = bar.fill;
                        if (chartId === 'fuel-per-hr-avg-fuel-stacked') {
                          if (bar.dataKey === 'totalTrips') {
                            highlightColor = entry.equipmentId === selectedEquipmentIdForDrillDown || entry.name === selectedEquipmentForDrillDown ? "#2563eb" : bar.fill;
                          } else if (bar.dataKey === 'totalFuel') {
                            highlightColor = entry.equipmentId === selectedEquipmentIdForDrillDown || entry.name === selectedEquipmentForDrillDown ? "#dc2626" : bar.fill;
                          }
                        }
                        return <Cell key={`cell-${i}`} fill={highlightColor} style={{ cursor: 'pointer' }} />;
                      })}
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Fixed X-axis label - positioned absolutely at the bottom */}
          {props.xAxis?.label && (
            <div
              className="absolute bottom-[40px] left-0 right-0 z-30 pointer-events-none flex justify-center items-center"
              style={{
                height: '30px',
                marginLeft: props.yAxis?.label ? '60px' : '0',
                marginRight: '20px',
                fontSize: '14px', // Increased from 12px
                fontWeight: 600,
                color: '#374151'
              }}
            >
              {props.xAxis.label.value || 'X-Axis'}
            </div>
          )}

          {/* Fixed Legend at bottom - positioned next to scrollbar */}
          {chartId === 'fuel-per-hr-avg-fuel-stacked' && (
            <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-6 px-6 pb-4" style={{ marginLeft: props.yAxis?.label ? '60px' : '0', marginRight: '20px' }}>
              <div className="flex items-center text-xs text-gray-600">
                <span className="w-3 h-3 rounded-full bg-[#60a5fa] mr-2 inline-block"></span>
                Total Trips
              </div>
              <div className="flex items-center text-xs text-gray-600">
                <span className="w-3 h-3 rounded-full bg-[#ef4444] mr-2 inline-block"></span>
                Total Fuel
              </div>
            </div>
          )}
        </div>
      );
    } else if (config.chartType === 'composed') {
      const props = config.chartProps;
      // Calculate minimum width based on number of bars
      const barWidth = 62.29;
      const estimatedWidthPerBar = 100; // Approximate space per bar including gap
      const minChartWidth = Math.max(1000, data.length * estimatedWidthPerBar);

      // Increase gap between bars in maximized view for better readability
      const maximizedBarCategoryGap = '25%';
      // Set bar width slightly smaller for maximized view to look less "oversized"
      const maximizedBarSize = 48;

      // Ensure margin includes left space for Y-axis labels
      const chartMargin = props.margin || {};
      const defaultMargin = {
        top: chartMargin.top ?? 5,
        right: chartMargin.right ?? 10,
        left: chartMargin.left ?? (props.yAxis?.label ? 50 : 10),
        bottom: chartMargin.bottom ?? 30
      };

      // Create modified axis props without labels (we'll render them separately as fixed overlays)
      const xAxisPropsWithoutLabel = { ...props.xAxis };
      delete xAxisPropsWithoutLabel.label;

      const yAxisPropsWithoutLabel = { ...props.yAxis };
      delete yAxisPropsWithoutLabel.label;

      return (
        <div className="w-full h-full relative flex flex-col" key={`chart-container-${chartId}-${modalSortOrder}`}>
          {/* Fixed Y-axis label - positioned absolutely on the left */}
          {props.yAxis?.label && (
            <div
              className="absolute left-0 top-0 bottom-0 z-30 pointer-events-none flex items-center"
              style={{
                width: '60px',
                paddingLeft: '15px'
              }}
            >
              <div
                style={{
                  writingMode: 'vertical-rl',
                  textOrientation: 'mixed',
                  transform: 'rotate(180deg)',
                  fontSize: '13px', // Increased from 11px
                  fontWeight: 600,
                  color: '#374151',
                  whiteSpace: 'nowrap'
                }}
              >
                {props.yAxis.label.value || 'Y-Axis'}
              </div>
            </div>
          )}

          {/* Scrollable chart container */}
          <div
            className="flex-1 overflow-x-auto overflow-y-hidden relative"
            style={{ marginLeft: props.yAxis?.label ? '60px' : '0', marginBottom: (props.xAxis?.label || chartId === 'fuel-per-hr-avg-fuel-composed') ? '70px' : '40px' }}
          >
            <div style={{ minWidth: `${minChartWidth}px`, width: '100%', height: '100%', position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%" key={`chart-${chartId}-${modalSortOrder}`}>
                <ComposedChart data={data} barCategoryGap={maximizedBarCategoryGap} margin={defaultMargin}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis {...xAxisPropsWithoutLabel} />
                  <YAxis {...yAxisPropsWithoutLabel} />
                  <Tooltip cursor={false} />
                  {props.referenceLine && (
                    <ReferenceLine
                      y={props.referenceLine.y}
                      yAxisId={props.referenceLine.yAxisId}
                      stroke={props.referenceLine.stroke}
                      strokeWidth={props.referenceLine.strokeWidth}
                    />
                  )}
                  {props.bars.map((bar, index) => (
                    <Bar
                      key={index}
                      dataKey={bar.dataKey}
                      fill={bar.fill}
                      radius={bar.radius}
                      barSize={chartId === 'fuel-per-hr-avg-fuel-composed' ? 62.29 : (bar.barSize || maximizedBarSize)}
                      activeBar={false}
                      onClick={(data, index, e) => {
                        if (e && e.stopPropagation) e.stopPropagation();
                        if (chartId === 'fuel-per-hr-avg-fuel-composed' && data && data.name) {
                          handleEquipmentDrillDown(data.name, data.equipmentId);
                        }
                      }}
                      cursor="pointer"
                    >
                      {data.map((entry, i) => (
                        <Cell
                          key={`cell-${i}`}
                          fill={entry.equipmentId === selectedEquipmentIdForDrillDown || entry.name === selectedEquipmentForDrillDown ? "#2563eb" : bar.fill}
                          style={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Bar>
                  ))}
                  {props.lines.map((line, index) => (
                    <Line
                      key={index}
                      type="monotone"
                      dataKey={line.dataKey}
                      stroke={line.stroke}
                      strokeWidth={line.strokeWidth}
                      dot={line.dot}
                    />
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Fixed X-axis label - positioned absolutely at the bottom */}
          {props.xAxis?.label && (
            <div
              className="absolute bottom-[40px] left-0 right-0 z-30 pointer-events-none flex justify-center items-center"
              style={{
                height: '30px',
                marginLeft: props.yAxis?.label ? '60px' : '0',
                marginRight: '20px',
                fontSize: props.xAxis.label.style?.fontSize || '12px',
                fontWeight: 500,
                color: '#374151'
              }}
            >
              {props.xAxis.label.value || 'X-Axis'}
            </div>
          )}

          {/* Fixed Legend at bottom - positioned next to scrollbar */}
          {chartId === 'fuel-per-hr-avg-fuel-composed' && (
            <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-6 px-6 pb-4" style={{ marginLeft: props.yAxis?.label ? '60px' : '0', marginRight: '20px' }}>
              <div className="flex items-center text-xs text-gray-600">
                <span className="w-3 h-3 rounded-full bg-[#60a5fa] mr-2 inline-block"></span>
                Fuel per hr
              </div>
              <div className="flex items-center text-xs text-gray-600">
                <span className="w-3 h-3 rounded-full bg-[#ef4444] mr-2 inline-block"></span>
                Avg fuel per hr
              </div>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  // Reusable Maximize Button Component
  const MaximizeButton = ({ chartId, className = "p-2 hover:bg-gray-100 rounded-lg transition-colors" }) => (
    <button
      onClick={() => handleMaximize(chartId)}
      className={className}
      aria-label="Maximize chart"
    >
      <Maximize2 className="w-4 h-4 text-gray-600" />
    </button>
  );

  // Render header actions (dropdown selectors) for maximized charts
  const renderHeaderActions = (chartId) => {
    const config = chartConfigs[chartId];
    if (!config) return null;

    // All charts should have the sort dropdown in modal
    return (
      <Select value={modalSortOrder} onValueChange={(value) => setModalSortOrder(value)}>
        <SelectTrigger className="w-[160px] h-8 text-xs bg-white">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="low-high">Low to High</SelectItem>
          <SelectItem value="high-low">High to Low</SelectItem>
        </SelectContent>
      </Select>
    );
  };

  return (
    <div className="flex flex-col min-h-full">
      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl flex items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      )}

      {/* Active Drill-down Filters - UI indication */}
      {(selectedEquipmentForDrillDown || selectedDriverForDrillDown) && (
        <div className="px-3 md:px-4 py-2 bg-gray-50 flex items-center gap-2">
          <div className="text-[12px] font-medium text-gray-500">Active Drill-down:</div>
          {selectedEquipmentForDrillDown && (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-2 py-0.5 rounded-full text-[11px] flex items-center gap-1.5 shadow-sm font-style-open-sans">
              <span className="font-bold">Equipment:</span> {selectedEquipmentForDrillDown}
              <button
                onClick={handleClearDrillDown}
                className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                title="Clear equipment filter"
              >
                <X className="w-3 h-3 text-blue-700" />
              </button>
            </div>
          )}
          {selectedDriverForDrillDown && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-2 py-0.5 rounded-full text-[11px] flex items-center gap-1.5 shadow-sm font-style-open-sans">
              <span className="font-bold">Driver:</span> {selectedDriverForDrillDown}
              <button
                onClick={handleClearDriverDrillDown}
                className="hover:bg-emerald-200 rounded-full p-0.5 transition-colors"
                title="Clear driver filter"
              >
                <X className="w-3 h-3 text-emerald-700" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Key Metrics Section */}
      <div className="px-3 md:px-4 pt-2 pb-1 bg-gray-50">
        {/* Top Metrics Row: Total Fuel Qty/Trips and Average Fuel Consumption */}
        <div className="flex flex-wrap gap-2">
          {/* Left Card: Total Fuel Qty and Total Trips */}
          <div className="bg-white rounded-lg shadow-sm p-2 border border-gray-200 min-w-[200px] flex-1 h-[80px]">
            <div className="grid grid-cols-2 gap-4">
              {/* Total Fuel Qty Section */}
              <div className="flex flex-col">
                <h3 className="text-xs font-bold font-style-open-sans text-neutral-950 mb-3">Total Fuel Qty</h3>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold font-style-open-sans text-neutral-950">
                    {summary.totalFuelQtyLitres.toLocaleString()}
                  </span>
                  <span className="text-[11px] text-gray-500">Litres</span>
                  <span
                    className="text-[10px] font-medium rounded px-1.5 py-0.5 bg-green-100 text-green-600"
                  >
                    {Math.abs(summary.fuelQtyChange).toFixed(1)}%
                  </span>
                </div>
              </div>


              {/* Total Trips Section
              <div className="flex flex-col border-l border-gray-200 pl-4">
                <h3 className="text-xs font-bold font-style-open-sans text-neutral-950 mb-3">Total Trips</h3>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold font-style-open-sans text-neutral-950">
                    {summary.totalTrips.toLocaleString()}
                  </span>
                  <span className="text-[11px] text-gray-500">Trips</span>
                  <span
                    className="text-[10px] font-medium rounded px-1.5 py-0.5 bg-green-100 text-green-600"
                  >
                    {Math.abs(summary.tripsChange).toFixed(1)}%
                  </span>
                </div>
              </div>
              */}
            </div>
          </div>

          {/* Right Card: Average Fuel Consumption */}
          <div className="bg-white rounded-lg shadow-sm p-2 border border-gray-200 min-w-[200px] flex-1 h-[80px]">
            <h3 className="text-xs font-bold font-style-open-sans text-neutral-950 mb-3">Average Fuel Consumption</h3>
            <div className="grid grid-cols-2 gap-4">
              {/* Litre Per Hour Section */}
              <div className="flex flex-col">
                <div className="flex items-center gap-1">
                  <span className="text-lg font-bold font-style-open-sans text-neutral-950">
                    {summary.avgFuelConsumption.litrePerHour.toFixed(2)}
                  </span>
                  <span className="text-[11px] text-gray-500">L/Hr</span>
                </div>
              </div>

              {/* Km Per Litre Section */}
              <div className="flex flex-col border-l border-gray-200 pl-4">
                <div className="flex items-center gap-1">
                  <span className="text-lg font-bold font-style-open-sans text-neutral-950">
                    {summary.avgFuelConsumption.kmPerLitre.toFixed(2)}
                  </span>
                  <span className="text-[11px] text-gray-500">Km/L</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* END KEY METRICS SECTION */}

      {/* Charts Section */}
      <div className="flex-1 px-3 md:px-4 pb-4 bg-gray-50">
        {/* All Charts - Responsive Grid */}
        <div className="grid gap-3 pt-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">

          {/* Total Fuel by Date */}
          <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
            <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-bold font-style-open-sans text-gray-900">Total Fuel by Date</h3>
              <div className="flex items-center gap-2">
                <Select defaultValue="low-high">
                  <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low-high">Low to High</SelectItem>
                    <SelectItem value="high-low">High to Low</SelectItem>
                  </SelectContent>
                </Select>
                <MaximizeButton chartId="total-fuel-by-date" />
              </div>
            </div>
            <div className="flex-1 flex items-center justify-center p-4 pb-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData.totalFuelByDate.slice(-30)} margin={{ top: 5, right: 20, left: 10, bottom: 3 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    label={{ value: 'Date', position: 'insideBottom', offset: -1, style: { textAnchor: 'middle', fontSize: 12 } }}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    label={{ value: 'Total Fuel', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 12 } }}
                  />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="current"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="forecast"
                    stroke="#f97316"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center gap-6 px-6 pb-2">
              <div className="flex items-center text-xs text-gray-600">
                <span className="w-3 h-3 rounded bg-[#3b82f6] mr-2 inline-block"></span>
                Current
              </div>
              <div className="flex items-center text-xs text-gray-600">
                <span className="w-3 h-3 rounded bg-[#f97316] mr-2 inline-block"></span>
                Forecast
              </div>
            </div>
          </div>

          {/* Total Fuel Per Hr by Date */}
          <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
            <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-bold font-style-open-sans text-gray-900">Total Fuel Per Hr by Date</h3>
              <div className="flex items-center gap-2">
                <Select defaultValue="low-high">
                  <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low-high">Low to High</SelectItem>
                    <SelectItem value="high-low">High to Low</SelectItem>
                  </SelectContent>
                </Select>
                <MaximizeButton chartId="total-fuel-per-hr-by-date" />
              </div>
            </div>
            <div className="flex-1 flex items-center justify-center p-4 pb-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData.totalFuelPerHrByDate.slice(-30)} margin={{ top: 5, right: 20, left: 10, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    label={{ value: 'Date', position: 'insideBottom', offset: -1, style: { textAnchor: 'middle', fontSize: 12 } }}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    label={{ value: 'Fuel Per Hr', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 12 } }}
                  />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="current"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="forecast"
                    stroke="#f97316"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center gap-6 px-6 pb-2">
              <div className="flex items-center text-xs text-gray-600">
                <span className="w-3 h-3 rounded bg-[#3b82f6] mr-2 inline-block"></span>
                Current
              </div>
              <div className="flex items-center text-xs text-gray-600">
                <span className="w-3 h-3 rounded bg-[#f97316] mr-2 inline-block"></span>
                Forecast
              </div>
            </div>
          </div>

          {/* Total Fuel Consumption by Driver Name - Horizontal Bar Chart */}
          <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
            <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-bold font-style-open-sans text-gray-900">Total Fuel Consumption by Driver Name</h3>
              <div className="flex items-center gap-2">
                <Select
                  value={sortOrder['total-fuel-consumption-by-driver'] || 'low-high'}
                  onValueChange={(value) => {
                    setSortOrder(prev => ({
                      ...prev,
                      'total-fuel-consumption-by-driver': value
                    }));
                  }}
                >
                  <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low-high">Low to High</SelectItem>
                    <SelectItem value="high-low">High to Low</SelectItem>
                  </SelectContent>
                </Select>
                <MaximizeButton chartId="total-fuel-consumption-by-driver" />
              </div>
            </div>
            <div className="flex-1 p-6 pt-4 flex flex-col overflow-hidden relative">
              {/* Fixed X-axis label */}
              <div className="absolute bottom-0 left-0 right-0 z-30 pointer-events-none flex justify-center items-center" style={{ height: '30px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                Consumption
              </div>
              {/* Fixed Y-axis label */}
              <div className="absolute left-5 top-0 bottom-14 z-30 pointer-events-none flex items-center" style={{ width: '60px', paddingLeft: '15px' }}>
                <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)', fontSize: '11px', fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>
                  Driver Name
                </div>
              </div>
              {/* Scrollable chart container */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden relative chart-scroll" style={{ marginBottom: '30px' }}>
                {(() => {
                  let data = chartData.totalFuelConsumptionByDriver || [];
                  // Apply sorting based on sortOrder state
                  const currentSortOrder = sortOrder['total-fuel-consumption-by-driver'] || 'low-high';
                  if (data.length > 0) {
                    data = [...data].sort((a, b) => {
                      const aValue = Number(a.consumption) || 0;
                      const bValue = Number(b.consumption) || 0;
                      return currentSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
                    });
                  }
                  const minChartHeight = Math.max(326, data.length * 60);
                  return (
                    <div style={{ minHeight: `${minChartHeight}px`, width: '100%', height: '100%' }} key={`chart-total-fuel-consumption-by-driver-${currentSortOrder}`}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={data}
                          layout="vertical"
                          margin={{ top: 5, right: 30, left: 0, bottom: 0 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                          <XAxis
                            type="number"
                            tick={{ fontSize: 12 }}
                            tickFormatter={(value) => `${value}k`}
                          />
                          <YAxis
                            type="category"
                            dataKey="label"
                            tick={{ fontSize: 11 }}
                            interval={0}
                            width={150}
                          />
                          <Tooltip cursor={false} content={renderDriverFuelTooltip} />
                          <Bar
                            dataKey="consumption"
                            fill="#3b82f6"
                            radius={[0, 4, 4, 0]}
                            barSize={34.94}
                            activeBar={false}
                            onClick={(data, index, e) => {
                              if (e && e.stopPropagation) e.stopPropagation();
                              if (data && data.operatorId) {
                                handleDriverDrillDown(data.label, data.operatorId);
                              } else if (data && data.label) {
                                // Fallback to name if operatorId is missing
                                handleDriverDrillDown(data.label, null);
                              }
                            }}
                            cursor="pointer"
                          >
                            {data.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={entry.operatorId === selectedOperatorIdForDrillDown ? "#1e40af" : "#3b82f6"}
                                style={{ cursor: 'pointer' }}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Fuel consumption per hr by Driver Name - Vertical Bar Chart */}
          <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
            <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-bold font-style-open-sans text-gray-900">Fuel consumption per hr by Driver Name</h3>
              <div className="flex items-center gap-2">
                <Select
                  value={sortOrder['fuel-consumption-per-hr-by-driver'] || 'low-high'}
                  onValueChange={(value) => {
                    setSortOrder(prev => ({
                      ...prev,
                      'fuel-consumption-per-hr-by-driver': value
                    }));
                  }}
                >
                  <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low-high">Low to High</SelectItem>
                    <SelectItem value="high-low">High to Low</SelectItem>
                  </SelectContent>
                </Select>
                <MaximizeButton chartId="fuel-consumption-per-hr-by-driver" />
              </div>
            </div>
            <div className="flex-1 p-6 pt-4 flex flex-col overflow-hidden relative">
              {/* Fixed Y-axis label */}
              <div className="absolute left-2 top-0 bottom-14 z-30 pointer-events-none flex items-center" style={{ width: '60px', paddingLeft: '15px' }}>
                <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)', fontSize: '11px', fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>
                  Fuel per Hr
                </div>
              </div>
              {/* Scrollable chart container */}
              <div className="flex-1 overflow-x-auto overflow-y-hidden relative chart-scroll" style={{ marginLeft: '60px', marginBottom: '-17px' }}>
                {(() => {
                  let data = chartData.fuelConsumptionPerHrByDriver || [];
                  // Apply sorting based on sortOrder state
                  const currentSortOrder = sortOrder['fuel-consumption-per-hr-by-driver'] || 'low-high';
                  if (data.length > 0) {
                    data = [...data].sort((a, b) => {
                      const aValue = Number(a.fuelPerHr) || 0;
                      const bValue = Number(b.fuelPerHr) || 0;
                      return currentSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
                    });
                  }
                  // Calculate dynamic width per bar based on longest label
                  const maxLabelLength = Math.max(...data.map(item => ((item.driverName || item.displayName) || '').length), 15);
                  const widthPerBar = Math.max(120, maxLabelLength * 10);
                  const minChartWidth = Math.max(512, data.length * widthPerBar);
                  return (
                    <div style={{ minWidth: `${minChartWidth}px`, width: '100%', height: '100%' }} key={`chart-fuel-consumption-per-hr-by-driver-${currentSortOrder}`}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} barCategoryGap="10%" margin={{ top: 5, right: 10, left: -5, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                          <XAxis
                            dataKey="driverName"
                            tick={{ fontSize: 12 }}
                            interval={0}
                            tickFormatter={(value) => value}
                          />
                          <YAxis
                            tick={{ fontSize: 12 }}
                            domain={[0, 10]}
                          />
                          <Tooltip cursor={false} content={renderDriverFuelTooltip} />
                          <Bar
                            dataKey="fuelPerHr"
                            fill="#ec4899"
                            radius={[4, 4, 0, 0]}
                            barSize={34.94}
                            activeBar={false}
                            onClick={(data, index, e) => {
                              if (e && e.stopPropagation) e.stopPropagation();
                              if (data && data.operatorId) {
                                handleDriverDrillDown(data.driverName, data.operatorId);
                              } else if (data && data.driverName) {
                                handleDriverDrillDown(data.driverName, null);
                              }
                            }}
                            cursor="pointer"
                          >
                            {data.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={entry.operatorId === selectedOperatorIdForDrillDown ? "#be185d" : "#ec4899"}
                                style={{ cursor: 'pointer' }}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })()}
              </div>
              {/* Fixed X-axis label */}
              <div className="absolute bottom-3 left-0 right-0 z-30 pointer-events-none flex justify-center items-center" style={{ height: '30px', marginLeft: '60px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                Driver Name
              </div>
            </div>
          </div>

          {/* Fuel per Hour and Productivity - Dual-Axis Area Chart */}
          <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
            <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-bold font-style-open-sans text-gray-900">Fuel per Hour and Productivity</h3>
              <div className="flex items-center gap-2">
                <Select defaultValue="low-high">
                  <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                    <SelectValue placeholder="Low to High" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low-high">Low to High</SelectItem>
                    <SelectItem value="high-low">High to Low</SelectItem>
                  </SelectContent>
                </Select>
                <MaximizeButton chartId="fuel-per-hour-and-productivity" />
              </div>
            </div>
            <div className="flex-1 p-6 pt-4">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData.fuelPerHourAndProductivity.slice(0, 2)} margin={{ top: 5, right: 25, left: 15, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorFuel" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#a855f7" stopOpacity={0.6} />
                      <stop offset="95%" stopColor="#a855f7" stopOpacity={0.1} />
                    </linearGradient>
                    <linearGradient id="colorProductivity" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#fbbf24" stopOpacity={0.6} />
                      <stop offset="95%" stopColor="#fbbf24" stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 11 }}
                    domain={[9.0, 9.4]}
                    label={{ value: 'Fuel per Hour', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 11 }}
                    domain={[440000, 460000]}
                    tickFormatter={(value) => `${value / 1000}K`}
                    label={{ value: 'Productivity', angle: 90, position: 'insideRight', style: { textAnchor: 'middle', fontSize: 11 } }}
                  />
                  <Tooltip />
                  <Area yAxisId="left" type="linear" dataKey="fuelPerHour" stroke="#a855f7" strokeWidth={2} fill="url(#colorFuel)" />
                  <Area yAxisId="right" type="linear" dataKey="productivity" stroke="#fbbf24" strokeWidth={2} fill="url(#colorProductivity)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Fuel per Km and Fuel Per Hr - Dual-Axis Area Chart */}
          <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
            <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-bold font-style-open-sans text-gray-900">Fuel per Km and Fuel Per Hr</h3>
              <div className="flex items-center gap-2">
                <Select defaultValue="low-high">
                  <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                    <SelectValue placeholder="Low to High" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low-high">Low to High</SelectItem>
                    <SelectItem value="high-low">High to Low</SelectItem>
                  </SelectContent>
                </Select>
                <MaximizeButton chartId="fuel-per-km-and-fuel-per-hr" />
              </div>
            </div>
            <div className="flex-1 p-6 pt-4">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData.fuelPerKmAndFuelPerHr.slice(0, 2)} margin={{ top: 15, right: 25, left: 20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorFuelKm" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#a855f7" stopOpacity={0.6} />
                      <stop offset="95%" stopColor="#a855f7" stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 11 }}
                    domain={[0.88, 0.90]}
                    label={{ value: 'Fuel Per Km', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 11 }}
                    domain={[9.0, 9.4]}
                    label={{ value: 'Fuel per Hour', angle: 90, position: 'insideRight', style: { textAnchor: 'middle', fontSize: 11 } }}
                  />
                  <Tooltip />
                  <Area yAxisId="left" type="linear" dataKey="fuelPerKm" stroke="#a855f7" strokeWidth={2} fill="url(#colorFuelKm)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Fuel per hr and Avg fuel per hour - Stacked Bar Chart */}
          <div className="bg-white rounded-lg shadow-sm p-0 border border-gray-200 h-[160px] flex flex-col">
            <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-bold text-gray-900">Fuel per hr and Avg fuel per hour</h3>
              <div className="flex items-center gap-3">
                <Select
                  value={sortOrder['fuel-per-hr-avg-fuel-stacked'] || 'low-high'}
                  onValueChange={(value) => {
                    setSortOrder(prev => ({
                      ...prev,
                      'fuel-per-hr-avg-fuel-stacked': value
                    }));
                  }}
                >
                  <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low-high">Low to High</SelectItem>
                    <SelectItem value="high-low">High to Low</SelectItem>
                  </SelectContent>
                </Select>
                <MaximizeButton chartId="fuel-per-hr-avg-fuel-stacked" />
              </div>
            </div>
            <div className="flex-1 p-6 pt-4 flex flex-col overflow-hidden relative">
              {/* Fixed Y-axis label */}
              <div className="absolute left-4 top-0 bottom-14 z-30 pointer-events-none flex items-center" style={{ width: '60px', paddingLeft: '15px' }}>
                <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)', fontSize: '11px', fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>
                  Fuel per Hr
                </div>
              </div>
              {/* Scrollable chart container */}
              <div className="flex-1 overflow-x-auto overflow-y-hidden relative chart-scroll" style={{ marginLeft: '60px', marginBottom: '15px' }}>
                {(() => {
                  let data = chartData.fuelPerHrAvgFuel || [];
                  // Apply sorting based on sortOrder state
                  const currentSortOrder = sortOrder['fuel-per-hr-avg-fuel-stacked'] || 'low-high';
                  if (data.length > 0) {
                    data = [...data].sort((a, b) => {
                      const aValue = Number(a.totalFuel || a.totalTrips) || 0;
                      const bValue = Number(b.totalFuel || b.totalTrips) || 0;
                      return currentSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
                    });
                  }
                  // Calculate dynamic width per bar based on longest label
                  const maxLabelLength = Math.max(...data.map(item => (item.name || '').length), 15);
                  const widthPerBar = Math.max(120, maxLabelLength * 10);
                  const minChartWidth = Math.max(341, data.length * widthPerBar);
                  return (
                    <div style={{ minWidth: `${minChartWidth}px`, width: '100%', height: '100%' }} key={`chart-fuel-per-hr-avg-fuel-stacked-${currentSortOrder}`}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} margin={{ top: 30, right: 10, left: 0, bottom: -5 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                          <XAxis
                            dataKey="name"
                            tick={{ fontSize: 11 }}
                            interval={0}
                          />
                          <YAxis
                            tick={{ fontSize: 11 }}
                            tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(1)}K` : value}
                          />
                          <Tooltip cursor={false} />
                          <Bar
                            dataKey="totalTrips"
                            stackId="a"
                            fill="#60a5fa"
                            radius={[0, 0, 0, 0]}
                            barSize={34.94}
                            barCategoryGap="10%"
                            activeBar={false}
                            onClick={(data) => handleEquipmentDrillDown(data.name, data.equipmentId)}
                            cursor="pointer"
                          >
                            {data.map((entry, index) => (
                              <Cell
                                key={`cell-trips-${index}`}
                                fill={entry.equipmentId === selectedEquipmentIdForDrillDown || entry.name === selectedEquipmentForDrillDown ? "#2563eb" : "#60a5fa"}
                                style={{ cursor: 'pointer' }}
                              />
                            ))}
                          </Bar>
                          <Bar
                            dataKey="totalFuel"
                            stackId="a"
                            fill="#ef4444"
                            radius={[4, 4, 0, 0]}
                            activeBar={false}
                            onClick={(data) => handleEquipmentDrillDown(data.name, data.equipmentId)}
                            cursor="pointer"
                          >
                            {data.map((entry, index) => (
                              <Cell
                                key={`cell-fuel-${index}`}
                                fill={entry.equipmentId === selectedEquipmentIdForDrillDown || entry.name === selectedEquipmentForDrillDown ? "#dc2626" : "#ef4444"}
                                style={{ cursor: 'pointer' }}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })()}
              </div>
              {/* Fixed X-axis label */}
              <div className="absolute bottom-4 left-0 right-0 z-30 pointer-events-none flex justify-center items-center" style={{ height: '30px', marginLeft: '60px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                Equipment
              </div>
              {/* Fixed Legend at bottom */}
              <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-6 px-6 pb-2" style={{ marginLeft: '60px' }}>
                <div className="flex items-center text-xs text-gray-600">
                  <span className="w-3 h-3 rounded-full bg-[#60a5fa] mr-2 inline-block"></span>
                  Total Trips
                </div>
                <div className="flex items-center text-xs text-gray-600">
                  <span className="w-3 h-3 rounded-full bg-[#ef4444] mr-2 inline-block"></span>
                  Total Fuel
                </div>
              </div>
            </div>
          </div>

          {/* Fuel per hr and Avg fuel per hour - Bar Chart with Reference Line */}
          <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
            <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-bold font-style-open-sans text-gray-900">Fuel per hr and Avg fuel per hour</h3>
              <div className="flex items-center gap-2">
                <Select
                  value={sortOrder['fuel-per-hr-avg-fuel-composed'] || 'low-high'}
                  onValueChange={(value) => {
                    setSortOrder(prev => ({
                      ...prev,
                      'fuel-per-hr-avg-fuel-composed': value
                    }));
                  }}
                >
                  <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low-high">Low to High</SelectItem>
                    <SelectItem value="high-low">High to Low</SelectItem>
                  </SelectContent>
                </Select>
                <MaximizeButton chartId="fuel-per-hr-avg-fuel-composed" />
              </div>
            </div>
            <div className="flex-1 p-6 pt-4 flex flex-col overflow-hidden relative">
              {/* Fixed Y-axis label */}
              <div className="absolute left-7 top-0 bottom-14 z-30 pointer-events-none flex items-center" style={{ width: '60px', paddingLeft: '15px' }}>
                <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)', fontSize: '11px', fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>
                  Fuel per Hr
                </div>
              </div>
              {/* Scrollable chart container */}
              <div className="flex-1 overflow-x-auto overflow-y-hidden relative chart-scroll" style={{ marginLeft: '60px', marginBottom: '15px' }}>
                {(() => {
                  let data = chartData.fuelPerHrAvgFuelComposed || [];
                  // Apply sorting based on sortOrder state
                  const currentSortOrder = sortOrder['fuel-per-hr-avg-fuel-composed'] || 'low-high';
                  if (data.length > 0) {
                    data = [...data].sort((a, b) => {
                      const aValue = Number(a.fuelPerHr) || 0;
                      const bValue = Number(b.fuelPerHr) || 0;
                      return currentSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
                    });
                  }
                  // Calculate dynamic width per bar based on longest label
                  const maxLabelLength = Math.max(...data.map(item => (item.name || '').length), 15);
                  const widthPerBar = Math.max(120, maxLabelLength * 10);
                  const minChartWidth = Math.max(512, data.length * widthPerBar);
                  return (
                    <div style={{ minWidth: `${minChartWidth}px`, width: '100%', height: '100%' }} key={`chart-fuel-per-hr-avg-fuel-composed-${currentSortOrder}`}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                          <XAxis
                            dataKey="name"
                            tick={{ fontSize: 12 }}
                            interval={0}
                          />
                          <YAxis
                            tick={{ fontSize: 12 }}
                            tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(1)}K` : value.toFixed(1)}
                          />
                          <Tooltip cursor={false} />
                          <Bar
                            dataKey="fuelPerHr"
                            fill="#60a5fa"
                            radius={[4, 4, 0, 0]}
                            barSize={34.94}
                            barCategoryGap="10%"
                            activeBar={false}
                            onClick={(data) => handleEquipmentDrillDown(data.name, data.equipmentId)}
                            cursor="pointer"
                          >
                            {data.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={entry.equipmentId === selectedEquipmentIdForDrillDown || entry.name === selectedEquipmentForDrillDown ? "#2563eb" : "#60a5fa"}
                                style={{ cursor: 'pointer' }}
                              />
                            ))}
                          </Bar>
                          <Line type="monotone" dataKey="avgFuelPerHr" stroke="#ef4444" strokeWidth={2} dot={false} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })()}
              </div>
              {/* Fixed X-axis label */}
              <div className="absolute bottom-4 left-0 right-0 z-30 pointer-events-none flex justify-center items-center" style={{ height: '30px', marginLeft: '60px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                Equipment
              </div>
              {/* Fixed Legend at bottom */}
              <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-6 px-6 pb-2" style={{ marginLeft: '60px' }}>
                <div className="flex items-center text-xs text-gray-600">
                  <span className="w-3 h-3 rounded-full bg-[#60a5fa] mr-2 inline-block"></span>
                  Fuel per hr
                </div>
                <div className="flex items-center text-xs text-gray-600">
                  <span className="w-3 h-3 rounded-full bg-[#ef4444] mr-2 inline-block"></span>
                  Avg fuel per hr
                </div>
              </div>
            </div>
          </div>

          {/* Fuel per km_total - Simple Bar Chart */}
          <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
            <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-bold font-style-open-sans text-gray-900">Fuel per km_total</h3>
              <div className="flex items-center gap-2">
                <Select
                  value={sortOrder['fuel-per-km-total'] || 'low-high'}
                  onValueChange={(value) => {
                    setSortOrder(prev => ({
                      ...prev,
                      'fuel-per-km-total': value
                    }));
                  }}
                >
                  <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low-high">Low to High</SelectItem>
                    <SelectItem value="high-low">High to Low</SelectItem>
                  </SelectContent>
                </Select>
                <MaximizeButton chartId="fuel-per-km-total" />
              </div>
            </div>
            <div className="flex-1 p-6 pt-4 flex flex-col overflow-hidden relative">
              {/* Fixed Y-axis label */}
              <div className="absolute left-8 top-0 bottom-14 z-30 pointer-events-none flex items-center" style={{ width: '60px', paddingLeft: '15px' }}>
                <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)', fontSize: '11px', fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>
                  Fuel per km_total
                </div>
              </div>
              {/* Scrollable chart container */}
              <div className="flex-1 overflow-x-auto overflow-y-hidden relative chart-scroll" style={{ marginLeft: '60px', marginBottom: '-17px' }}>
                {(() => {
                  let data = chartData.fuelPerKmTotal || [];
                  // Apply sorting based on sortOrder state
                  const currentSortOrder = sortOrder['fuel-per-km-total'] || 'low-high';
                  if (data.length > 0) {
                    data = [...data].sort((a, b) => {
                      const aValue = Number(a.fuelPerKmTotal) || 0;
                      const bValue = Number(b.fuelPerKmTotal) || 0;
                      return currentSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
                    });
                  }
                  // Calculate dynamic width per bar based on longest label
                  const maxLabelLength = Math.max(...data.map(item => (item.name || '').length), 15);
                  const widthPerBar = Math.max(120, maxLabelLength * 10);
                  const minChartWidth = Math.max(512, data.length * widthPerBar);
                  return (
                    <div style={{ minWidth: `${minChartWidth}px`, width: '100%', height: '100%' }} key={`chart-fuel-per-km-total-${currentSortOrder}`}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} barCategoryGap="10%" margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                          <XAxis
                            dataKey="name"
                            tick={{ fontSize: 12 }}
                            interval={0}
                          />
                          <YAxis
                            tick={{ fontSize: 12 }}
                          />
                          <Tooltip cursor={false} />
                          <Bar
                            dataKey="fuelPerKmTotal"
                            fill="#a855f7"
                            radius={[4, 4, 0, 0]}
                            barSize={34.94}
                            activeBar={false}
                            onClick={(data) => handleEquipmentDrillDown(data.name, data.equipmentId)}
                            cursor="pointer"
                          >
                            {data.map((entry, index) => (entry && (
                              <Cell
                                key={`cell-${index}`}
                                fill={entry.equipmentId === selectedEquipmentIdForDrillDown || entry.name === selectedEquipmentForDrillDown ? "#7e22ce" : "#a855f7"}
                                style={{ cursor: 'pointer' }}
                              />
                            )))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })()}
              </div>
              {/* Fixed X-axis label */}
              <div className="absolute bottom-2 left-0 right-0 z-30 pointer-events-none flex justify-center items-center" style={{ height: '30px', marginLeft: '60px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                Equipment
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chart Modal for Maximized View */}
      {
        maximizedChartId && (
          <ChartModal
            isOpen={!!maximizedChartId}
            onClose={handleCloseModal}
            title={chartConfigs[maximizedChartId]?.title || 'Chart'}
            headerActions={renderHeaderActions(maximizedChartId)}
            showEquipmentFilter={!['total-fuel-by-date', 'total-fuel-per-hr-by-date'].includes(maximizedChartId)}
            equipmentList={getEquipmentListForChart(maximizedChartId)}
            selectedEquipment={modalEquipmentFilter}
            onEquipmentChange={setModalEquipmentFilter}
            filterLabel={['total-fuel-consumption-by-driver', 'fuel-consumption-per-hr-by-driver'].includes(maximizedChartId) ? 'All Drivers' : 'All Equipment'}
          >
            {renderMaximizedChart(maximizedChartId)}
          </ChartModal>
        )
      }
    </div >
  );
};

export default Fuel;
